import { LandingPageStyle } from "./styles/LandingPageStyle";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useRef, useState } from "react";
import posterLogo from "../assets/images/readwell-logo.svg";

const landingVideoUrl = "https://readrivals-dashboard-video.s3.us-east-2.amazonaws.com/FINAL%20SQUARE%20VERSION.mp4";

const LandingPage = () => {
    const { credObj } = useSelector((store) => store.auth);
    const dashboardHref = credObj ? "/landing" : "/welcome";
    const videoRef = useRef(null);
    const [showCover, setShowCover] = useState(true);

    const handlePlayClick = async () => {
        if (!videoRef.current) return;
        try {
            await videoRef.current.play();
            setShowCover(false);
        } catch (_error) {
            // Ignore play interruption errors and keep cover visible.
        }
    };

    return (
        <LandingPageStyle>
            {/* Header */}
            <header>
                <div className="header-container">
                    <Link to="/landing" className="logo">
                        ReadRivals
                    </Link>
                    <nav>
                        <Link to={dashboardHref}>
                            Teacher / School Dashboard
                        </Link>
                    </nav>
                </div>
            </header>

            {/* Hero Section */}
            <section className="hero">
                <div className="hero-container">
                    <h1>Read Smarter. Read Faster. Read Confidently.</h1>
                </div>
            </section>

            {/* Content Section */}
            <section className="content-section">
                <div className="content-container">
                    <div className="video-shell">
                        <video
                            ref={videoRef}
                            className="landing-video"
                            controls
                            playsInline
                            preload="metadata"
                            onPlay={() => setShowCover(false)}
                            onEnded={() => setShowCover(true)}
                        >
                            <source src={landingVideoUrl} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                        {showCover && (
                            <button
                                type="button"
                                className="video-cover"
                                onClick={handlePlayClick}
                                aria-label="Play ReadRivals demo video"
                            >
                                <img src={posterLogo} alt="ReadRivals" className="cover-logo" />
                                <div className="cover-copy">
                                    <span className="cover-chip">Product Demo</span>
                                    <h3>Watch ReadRivals in action</h3>
                                </div>
                                <span className="play-badge">Play</span>
                            </button>
                        )}
                    </div>

                    {/* Features */}
                    <div className="features">
                        <h2>Transform Your Reading Experience</h2>
                        <ul>
                            <li>
                                <svg
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                                <div>
                                    <h3>Personalized Learning</h3>
                                    <p>
                                        Adaptive reading programs tailored to each student's unique pace and level
                                    </p>
                                </div>
                            </li>
                            <li>
                                <svg
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                                <div>
                                    <h3>Real-Time Progress Tracking</h3>
                                    <p>
                                        Monitor reading comprehension and speed with detailed analytics and insights
                                    </p>
                                </div>
                            </li>
                            <li>
                                <svg
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                                <div>
                                    <h3>Engaging Content Library</h3>
                                    <p>
                                        Access thousands of books and articles designed to inspire and motivate readers
                                    </p>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* App Badges Section */}
            <section className="app-badges">
                <div className="badges-container">
                    <div className="badges-header">
                        <h2>Download ReadRivals Today</h2>
                    </div>
                    <div className="badges-wrapper">
                        {/* App Store Badge */}
                        <a href="#" aria-label="Download on the App Store">
                            <div className="badge-button">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" />
                                </svg>
                                <div className="badge-text">
                                    <div className="badge-small">Download on the</div>
                                    <div className="badge-large">App Store</div>
                                </div>
                            </div>
                        </a>

                        {/* Google Play Badge */}
                        <a href="https://play.google.com/store/apps/details?id=com.readwell.adult" aria-label="Get it on Google Play">
                            <div className="badge-button">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                                </svg>
                                <div className="badge-text">
                                    <div className="badge-small">GET IT ON</div>
                                    <div className="badge-large">Google Play</div>
                                </div>
                            </div>
                        </a>
                    </div>
                </div>
            </section>

            <footer className="site-footer">
                <div className="footer-container">
                    <p>© {new Date().getFullYear()} ReadRivals LLC. All rights reserved.</p>
                    <div className="footer-links">
                        <Link to="/privacypolicy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                        <Link to="/termscondition" target="_blank" rel="noopener noreferrer">Terms & Conditions</Link>
                        <Link to="/dmca" target="_blank" rel="noopener noreferrer">Report Copyright Infringement (DMCA)</Link>
                    </div>
                </div>
            </footer>
        </LandingPageStyle>
    );
};

export default LandingPage;
